import React, { useCallback, useEffect, useState } from 'react';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { servicesService } from '../../services';
import { CustomDomain } from '../../services/services.service';
import { useNotificationContext } from '../../contexts/NotificationContext';
import ConfirmationModal from './ConfirmationModal/ConfirmationModal';

/**
 * Componente global de dominios personalizados para servicios de marketplace EC2.
 * Reutiliza la Lambda `custom_domain` (extendida para soportar servicios sin
 * appId de app-deploy): registra el dominio (crea cert ACM DNS-validation),
 * muestra los registros DNS a configurar, y verifica (adjunta cert al ALB +
 * agrega host-header a la regla del servicio; para WordPress fija el siteurl).
 *
 * El usuario NO pasa appId: al omitirlo, el backend trata el registro como
 * servicio de marketplace y toma el routing del registro del servicio.
 */

interface CustomDomainsTabProps {
  serviceId: string;
  /** fqdn del servicio (ej: svc-xxx.apps2.kubiy.site). Si falta, el routing aún no está listo. */
  fqdn?: string | null;
}

const isValidDomain = (raw: string): boolean => {
  const d = raw.trim().toLowerCase();
  if (!d || d.length > 253) return false;
  if (/\s|https?:\/\/|\/|:|\*/.test(d)) return false;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(d)) return false; // no IPs
  if (d.endsWith('.kubiy.site')) return false;
  const labels = d.split('.');
  if (labels.length < 2) return false;
  const labelRe = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;
  if (!labels.every((l) => labelRe.test(l))) return false;
  const tld = labels[labels.length - 1];
  return /^[a-z]{2,63}$/.test(tld);
};

const statusMeta = (status?: string): { label: string; className: string; icon: React.ReactNode } => {
  const s = (status || '').toUpperCase();
  if (s === 'ACTIVE') {
    return {
      label: 'Activo',
      className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      icon: <CheckCircleOutlined />,
    };
  }
  if (s === 'FAILED') {
    return {
      label: 'Falló',
      className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      icon: <InfoCircleOutlined />,
    };
  }
  return {
    label: s === 'WAITING_DNS' ? 'Esperando DNS' : s === 'WAITING_CERTIFICATE' ? 'Validando certificado' : (status || 'Pendiente'),
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    icon: <ClockCircleOutlined />,
  };
};

const CustomDomainsTab: React.FC<CustomDomainsTabProps> = ({ serviceId, fqdn }) => {
  const { success, error: showError } = useNotificationContext();

  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [domainToDelete, setDomainToDelete] = useState<CustomDomain | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!serviceId) return;
    setIsLoading(true);
    try {
      const list = await servicesService.getCustomDomains(serviceId);
      setDomains(list);
    } catch (err: any) {
      // 404 / lista vacía no es error para el usuario
      setDomains([]);
    } finally {
      setIsLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    success('Copiado');
    setTimeout(() => setCopiedField((c) => (c === field ? null : c)), 1500);
  };

  const handleAdd = async () => {
    const domain = newDomain.trim().toLowerCase();
    if (!isValidDomain(domain)) {
      setInputError('Dominio inválido. Usa un dominio como app.tudominio.com (no puede ser .kubiy.site ni una IP).');
      return;
    }
    if (domains.some((d) => d.domainName?.toLowerCase() === domain)) {
      setInputError('Ese dominio ya está registrado.');
      return;
    }
    setInputError(null);
    setIsAdding(true);
    try {
      const created = await servicesService.registerCustomDomain(serviceId, { domainName: domain });
      setDomains((prev) => [created, ...prev]);
      setNewDomain('');
      success('Dominio registrado. Configura los registros DNS y luego verifica.');
    } catch (err: any) {
      showError(err?.response?.data?.message || err?.message || 'Error al registrar el dominio.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleVerify = async (domain: CustomDomain) => {
    setVerifyingId(domain.domainId);
    try {
      const updated = await servicesService.verifyCustomDomain(serviceId, undefined, domain.domainId, domain);
      if (updated) {
        setDomains((prev) => prev.map((d) => (d.domainId === updated.domainId ? updated : d)));
        if ((updated.status || '').toUpperCase() === 'ACTIVE') {
          success('Dominio activo. Ya apunta a tu servicio.');
        } else {
          success('Estado actualizado. Revisa los registros DNS pendientes.');
        }
      }
    } catch (err: any) {
      showError(err?.response?.data?.message || err?.message || 'Error al verificar el dominio.');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!domainToDelete) return;
    setIsDeleting(true);
    try {
      await servicesService.deleteCustomDomain(serviceId, domainToDelete.domainId);
      setDomains((prev) => prev.filter((d) => d.domainId !== domainToDelete.domainId));
      success('Dominio eliminado.');
      setDomainToDelete(null);
    } catch (err: any) {
      showError(err?.response?.data?.message || err?.message || 'Error al eliminar el dominio.');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderDnsRecord = (label: string, record: { type?: string; name?: string; value?: string } | undefined, keyPrefix: string) => {
    if (!record || !record.name || !record.value) return null;
    return (
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-2">
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{label}</p>
        <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
          <span className="text-gray-500 dark:text-gray-400">Tipo</span>
          <span className="font-mono text-gray-900 dark:text-white">{record.type || 'CNAME'}</span>
          <span className="text-gray-500 dark:text-gray-400">Nombre</span>
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-gray-900 dark:text-white truncate">{record.name}</span>
            <button onClick={() => copy(record.name!, `${keyPrefix}-name`)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0">
              {copiedField === `${keyPrefix}-name` ? <CheckCircleOutlined className="text-green-500" /> : <CopyOutlined />}
            </button>
          </div>
          <span className="text-gray-500 dark:text-gray-400">Valor</span>
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-gray-900 dark:text-white truncate">{record.value}</span>
            <button onClick={() => copy(record.value!, `${keyPrefix}-value`)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0">
              {copiedField === `${keyPrefix}-value` ? <CheckCircleOutlined className="text-green-500" /> : <CopyOutlined />}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!fqdn) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-r-lg">
        <div className="flex items-start gap-2">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 text-lg mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">Dominios no disponibles todavía</p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              El servicio aún no tiene su ruteo asignado. Vuelve cuando esté en estado{' '}
              <span className="font-mono">running</span> para configurar un dominio personalizado.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Dominios personalizados</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Conecta tu propio dominio. Registra el dominio, crea los registros DNS que se muestran y luego pulsa Verificar.
        </p>
      </div>

      {/* Agregar dominio */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-start">
        <div className="flex-1">
          <input
            type="text"
            value={newDomain}
            onChange={(e) => { setNewDomain(e.target.value); setInputError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !isAdding) handleAdd(); }}
            placeholder="app.tudominio.com"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
          />
          {inputError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{inputError}</p>}
        </div>
        <button
          onClick={handleAdd}
          disabled={isAdding || !newDomain.trim()}
          className="px-5 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 justify-center"
        >
          {isAdding ? <LoadingOutlined className="animate-spin" /> : <PlusOutlined />}
          Agregar dominio
        </button>
      </div>

      {/* Lista de dominios */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <LoadingOutlined className="text-2xl" spin />
        </div>
      ) : domains.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6 text-center">
          <GlobalOutlined className="text-2xl text-gray-400 dark:text-gray-500" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Aún no hay dominios personalizados.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {domains.map((domain) => {
            const meta = statusMeta(domain.status);
            const isActive = (domain.status || '').toUpperCase() === 'ACTIVE';
            return (
              <div key={domain.domainId} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <GlobalOutlined className="text-gray-400 flex-shrink-0" />
                    <span className="font-mono text-sm text-gray-900 dark:text-white truncate">{domain.domainName}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.className}`}>
                      {meta.icon} {meta.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleVerify(domain)}
                      disabled={verifyingId === domain.domainId}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {verifyingId === domain.domainId ? <LoadingOutlined className="animate-spin" /> : <ReloadOutlined />}
                      Verificar
                    </button>
                    <button
                      onClick={() => setDomainToDelete(domain)}
                      className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      title="Eliminar"
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                </div>

                {domain.message && !isActive && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{domain.message}</p>
                )}

                {!isActive && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {renderDnsRecord('1. Validación del certificado (CNAME)', domain.dnsInstructions?.certificateValidation, `${domain.domainId}-cert`)}
                    {renderDnsRecord('2. Tráfico (CNAME)', domain.dnsInstructions?.traffic, `${domain.domainId}-traffic`)}
                  </div>
                )}

                {isActive && (
                  <p className="text-xs text-green-700 dark:text-green-400">
                    El dominio está activo y apuntando a tu servicio.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmationModal
        isOpen={!!domainToDelete}
        onClose={() => setDomainToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Eliminar dominio"
        message={`¿Seguro que quieres eliminar el dominio ${domainToDelete?.domainName}? Dejará de apuntar a tu servicio.`}
        confirmText="Eliminar"
        loading={isDeleting}
        variant="danger"
      />
    </div>
  );
};

export default CustomDomainsTab;
